import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { SignalBadge } from '@/components/SignalBadge';
import type { EnrichedOpportunity } from '@/models/marketData';
import { formatCurrency, formatNumber } from '@/utils/formatters';

interface OpportunityTableProps {
  opportunities: EnrichedOpportunity[];
  totalCount: number;
  version: string;
  onSelect: (opportunity: EnrichedOpportunity) => void;
}

const SortIcon = ({ direction }: { direction: false | 'asc' | 'desc' }) => {
  if (direction === 'asc') return <ArrowUp size={13} aria-hidden="true" />;
  if (direction === 'desc') return <ArrowDown size={13} aria-hidden="true" />;
  return <ArrowUpDown size={13} aria-hidden="true" />;
};

export const OpportunityTable = ({
  opportunities,
  totalCount,
  onSelect,
}: OpportunityTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'score', desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  useEffect(() => {
    setPagination((current) => {
      const pageIndex = Math.min(
        current.pageIndex,
        Math.max(0, Math.ceil(opportunities.length / current.pageSize) - 1),
      );
      return pageIndex === current.pageIndex ? current : { ...current, pageIndex };
    });
  }, [opportunities.length]);
  const columns = useMemo<ColumnDef<EnrichedOpportunity>[]>(
    () => [
      {
        id: 'symbol',
        accessorFn: (row) => row.tradeInstrument.symbol,
        header: 'Symbol',
        cell: ({ row }) => (
          <div className="symbol-cell">
            <strong>{row.original.tradeInstrument.symbol}</strong>
            <span>{row.original.tradeInstrument.exchange}</span>
          </div>
        ),
      },
      {
        id: 'company',
        accessorFn: (row) => row.tradeInstrument.name,
        header: 'Company',
        cell: ({ getValue }) => (
          <span className="company-cell" title={getValue<string>()}>
            {getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: 'signal',
        header: 'Research signal',
        cell: ({ row }) => <SignalBadge signal={row.original.signal} />,
      },
      {
        accessorKey: 'score',
        header: 'Score',
        cell: ({ getValue }) => <strong className="score-cell">{getValue<number>()}</strong>,
      },
      {
        accessorKey: 'confidence',
        header: 'Confidence',
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <div className="confidence-cell">
              <span>{formatNumber(value, 1)}%</span>
              <i>
                <b style={{ width: `${Math.min(100, value)}%` }} />
              </i>
            </div>
          );
        },
      },
      {
        id: 'close',
        accessorFn: (row) => row.indicators.close,
        header: 'Close',
        cell: ({ getValue }) => formatCurrency(getValue<number>()),
      },
      {
        accessorKey: 'suggestedEntry',
        header: 'Watch zone',
        cell: ({ getValue }) => formatCurrency(getValue<number>()),
      },
      {
        accessorKey: 'stopLoss',
        header: 'Risk reference',
        cell: ({ getValue }) => (
          <span className="negative">{formatCurrency(getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'target1',
        header: 'Scenario 1',
        cell: ({ getValue }) => (
          <span className="positive">{formatCurrency(getValue<number>())}</span>
        ),
      },
      {
        id: 'rsi',
        accessorFn: (row) => row.indicators.rsi14,
        header: 'RSI',
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return <span className={value >= 70 ? 'warning' : ''}>{formatNumber(value, 1)}</span>;
        },
      },
      {
        id: 'adx',
        accessorFn: (row) => row.indicators.adx14,
        header: 'ADX',
        cell: ({ getValue }) => formatNumber(getValue<number>(), 1),
      },
      {
        id: 'volume',
        accessorFn: (row) => row.indicators.volumeRatio20,
        header: 'Volume',
        cell: ({ getValue }) => `${formatNumber(getValue<number>(), 2)}×`,
      },
      {
        id: 'superTrend',
        accessorFn: (row) => row.indicators.superTrendBullish,
        header: 'SuperTrend',
        cell: ({ getValue }) => (
          <span
            className={
              getValue<boolean>() ? 'state-chip state-chip--good' : 'state-chip state-chip--bad'
            }
          >
            {getValue<boolean>() ? 'Bullish' : 'Bearish'}
          </span>
        ),
      },
      {
        id: 'ema',
        accessorFn: (row) => row.metrics.emaAligned,
        header: 'EMA',
        cell: ({ getValue }) => (
          <span className={getValue<boolean>() ? 'positive' : 'muted'}>
            {getValue<boolean>() ? 'Aligned' : 'Mixed'}
          </span>
        ),
      },
      {
        id: 'macd',
        accessorFn: (row) => row.metrics.macdBullish,
        header: 'MACD',
        cell: ({ row }) => (
          <span className={row.original.metrics.macdBullish ? 'positive' : 'warning'}>
            {row.original.metrics.freshMacdCrossover
              ? 'Fresh cross'
              : row.original.metrics.macdBullish
                ? 'Bullish'
                : 'Below signal'}
          </span>
        ),
      },
      {
        id: 'newsSentiment',
        accessorFn: (row) => row.metrics.newsSentiment.score ?? -2,
        header: 'News',
        cell: ({ row }) => (
          <span
            className={`sentiment-chip sentiment-chip--${row.original.metrics.newsSentiment.type}`}
          >
            {row.original.metrics.newsSentiment.label}
            {row.original.relatedNews.length > 0 && <b>{row.original.relatedNews.length}</b>}
          </span>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: opportunities,
    autoResetPageIndex: false,
    getRowId: (row) => row.tradeInstrument.instrumentKey,
    columns,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  const rows = table.getRowModel().rows;

  return (
    <>
      <div className="table-scroll">
        <table className="opportunity-table">
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder ? null : (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        disabled={!header.column.getCanSort()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <SortIcon direction={header.column.getIsSorted()} />
                        )}
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.original.tradeInstrument.instrumentKey}
                tabIndex={0}
                onClick={() => onSelect(row.original)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(row.original);
                  }
                }}
                aria-label={`Open ${row.original.tradeInstrument.symbol} details`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="table-empty">
            <SearchIcon />
            <strong>No matching opportunities</strong>
            <span>Adjust or reset filters to widen the radar.</span>
          </div>
        )}
      </div>
      <div className="table-footer">
        <span>
          Showing {rows.length === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1}–
          {Math.min((pagination.pageIndex + 1) * pagination.pageSize, opportunities.length)} of{' '}
          {opportunities.length} filtered · {totalCount} total
        </span>
        <div className="pagination-controls">
          <label>
            Rows
            <select
              value={pagination.pageSize}
              onChange={(event) => table.setPageSize(Number(event.target.value))}
            >
              {[10, 20, 40].map((size) => (
                <option value={size} key={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <span>
            Page {table.getPageCount() === 0 ? 0 : pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </span>
          <button
            type="button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </>
  );
};

const SearchIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.7" />
    <path d="m20 20-4-4" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);
